
import type TTFiltering from '../../cms/client/TTFiltering';
import type TTQuery from '../../cms/client/TTQuery';
import type { CategoryLabel } from '../../common/categories/categoryLabel';
import { CategoryLabels } from '../../common/categories/categoryLabels';
import type { FilterParameters } from './ko/runtime/cardsWallFilterParameters';

export enum CardsWallEndpointType {
    DEFAULT = 'Default',
    SUMMARY = 'Summary',
    CAREERS_EVENT = 'Careers Event',
}

export interface QueryInput {
    siteKey: string;
    contentType?: string;
    sections?: string[];
    searchInput?: string;
    dateField?: string;
    startDate?: string;
    endDate?: string;
    offset: number;
    limit: number;
    orderingField: string;
    orderingType: string;
    include?: string[];
    services?: string[];
    industries?: string[];
    impacts?: string[];
    blacklist?: number[];
    whitelist?: number[];
    tags?: string[];
    alternativeResource?: string;
    homepage?: boolean;
    darkTheme?: boolean;
    endpointType: string[];

    country?: string[];
}



    export function makeQuery({
        siteKey,
        sections,
        services,
        industries,
        impacts,
        searchInput,
        dateField,
        startDate,
        endDate,
        offset,
        limit,
        orderingField,
        orderingType,
        include,
        blacklist,
        whitelist,
        tags,
        alternativeResource,
        country,
        homepage = false,
        endpointType,
    }: QueryInput): TTQuery {

        const filtering: TTFiltering[] = [{
            columnField: 'groupExternal',
            operatorValue: 'equals',
            value: siteKey
        }, {
            columnField: 'permission',
            operatorValue: 'equals',
            value: 'PUBLIC'
        }, {
            columnField: 'type',
            operatorValue: 'equals',
            value: endpointType
        }];

        if (homepage) {
            filtering.push({
                columnField: 'metaOptionValueMetaKey',
                operatorValue: 'equals',
                value: "homepage"
            });
        } else if (!endpointType.includes('summary')) {
            if (sections?.length) {
                filtering.push({
                    columnField: 'sectionPermalink',
                    operatorValue: 'equals',
                    value: sections
                });
            }
    
            if (tags?.length) {
                filtering.push({
                    columnField: 'tag',
                    operatorValue: 'equals',
                    value: tags
                });
            }
    
            if (services?.length) {
                filtering.push({
                    columnField: 'metaOptionValueMetaKey',
                    operatorValue: 'equals',
                    value: "services"
                }, {
                    columnField: 'metaOptionValueOptionKey',
                    operatorValue: 'equals',
                    value: services
                });
            }
    
            if (industries?.length) {
                filtering.push({
                    columnField: 'metaOptionValueMetaKey',
                    operatorValue: 'equals',
                    value: "industries"
                }, {
                    columnField: 'metaOptionValueOptionKey',
                    operatorValue: 'equals',
                    value: industries
                });
            }

            if (impacts?.length) {
                filtering.push({
                    columnField: 'metaOptionValueMetaKey',
                    operatorValue: 'equals',
                    value: "business_impact"
                }, {
                    columnField: 'metaOptionValueOptionKey',
                    operatorValue: 'equals',
                    value: impacts
                });
            }

            if (country?.length) {
                filtering.push({
                    columnField: 'country',
                    operatorValue: 'equals',
                    value: country
                });
            }

            if (searchInput?.length) {
                filtering.push({
                    columnField: 'title',
                    operatorValue: 'contains',
                    value: searchInput
                });
            }
    
            if (dateField && (startDate || endDate)) {
                if (startDate) {
                    filtering.push({
                        columnField: dateField,
                        operatorValue: 'onOrAfter',
                        value: startDate
                    });
                }
                if (endDate) {
                    filtering.push({
                        columnField: dateField,
                        operatorValue: 'onOrBefore',
                        value: endDate
                    });
                }
            }
        }

        if (whitelist?.length) {
            filtering.push({
                columnField: 'id',
                operatorValue: 'equals',
                value: whitelist.map(id => `${id}`),
            });
        } else if (blacklist?.length) {
            for (const id of blacklist) {
                filtering.push({
                    columnField: 'id',
                    operatorValue: 'not',
                    value: `${id}`
                });
            }
        }

        const query: TTQuery = {
            firstItem: offset,
            pageSize: limit,
            sorting: {
                field: orderingField,
                sort: orderingType
            },
            filtering,
            distinct: true
        };
        if (include?.length) {
            query.include = include;
        }
        if (alternativeResource) {
            query.alternativeResource = alternativeResource;
        }
        return query;
    }

    export function overrideQueryInput(queryInput: QueryInput, filterParameters: FilterParameters): QueryInput {
        if (filterParameters) {
            if (filterParameters.type) {
                const label: CategoryLabel[] = CategoryLabels.get(filterParameters.type);
                if (label.length > 0) {
                    queryInput.sections = label.map(l => l.key);
                }
            }
            if (filterParameters.text) {
                queryInput.searchInput = filterParameters.text;
            }
            if (filterParameters.date) {
                queryInput.dateField = 'sortDate';
                queryInput.startDate = filterParameters.formattedStartDate();
                queryInput.endDate = filterParameters.formattedEndDate();
            }
        }
        return queryInput;
    }
